import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';

import { ConfirmDialogueModal } from '../../../../components/modals/confirm-dialogue-modal';
import { useModal } from '../../../../hooks/use-modal';
import SsoProviderImage from './sso-provider-image';
import style from './sso-account-management.module.less';
import messages from './messages';

export const SsoAccountManagementRow = ({ idp, identities, isNativeSsoSeller, onUnlinkClick, onLinkClick }) => {
  const { open: openSsoDisconnectConfirm, isOpen: isSsoDisconnectConfirmOpen, close: closeSsoDisconnectConfirm } = useModal();
  const {
    open: openDoubleSsoDisconnectConfirm,
    isOpen: isDoubleSsoDisconnectConfirmOpen,
    close: closeDoubleSsoDisconnectConfirm,
  } = useModal();
  const { id, name, logoUrl } = idp;
  const linkedIdp = identities?.find((a) => a.idp.id === idp.id);

  const actionHandler = linkedIdp ? openSsoDisconnectConfirm : onLinkClick;
  const email = linkedIdp?.profileData?.email || '';
  const handlerArg = linkedIdp ?? idp;
  const actionText = linkedIdp ? messages.disconnect : messages.connect;
  const needsNativeSellerDoubleConfirm = useCallback(() => {
    // is a native sso user with a single connection (that they may be about to sever)
    return isNativeSsoSeller && identities.length === 1;
  }, [isNativeSsoSeller, identities]);
  const onClick = useCallback(() => {
    actionHandler(handlerArg);
  }, [actionHandler, handlerArg]);
  const onDisconnectConfirmClick = useCallback(() => {
    if (needsNativeSellerDoubleConfirm()) {
      openDoubleSsoDisconnectConfirm();
    } else {
      onUnlinkClick(handlerArg);
    }
    closeSsoDisconnectConfirm();
  }, [onUnlinkClick, handlerArg, closeSsoDisconnectConfirm, openDoubleSsoDisconnectConfirm, needsNativeSellerDoubleConfirm]);
  const onDoubleDisconnectConfirmClick = useCallback(() => {
    onUnlinkClick(handlerArg);
    closeDoubleSsoDisconnectConfirm();
  }, [onUnlinkClick, handlerArg, closeDoubleSsoDisconnectConfirm]);

  const ConfirmModalBody = () => {
    return (
      <div>
        <Text formattedMessageValues={{ idpName: idp.name }} textType={TextType.H4} value={messages.unlinkConfirmationWarningMessage} />
        <br />
        <Text
          formattedMessageValues={{ email, idpName: idp.name }}
          textType={TextType.Paragraph}
          value={messages.unlinkConfirmationMessage}
        />
      </div>
    );
  };

  // Double confirm for native SSO Users to ensure that a user does not remove their only access to the SS account
  // This is temporary until we implement logic to simply prevent this from being an option al all...
  const DoubleConfirmModalBody = () => {
    return (
      <div>
        <Text formattedMessageValues={{ idpName: idp.name }} textType={TextType.H4} value={messages.unlinkConfirmationWarningMessage} />
        <br />
        <Text
          formattedMessageValues={{ email, idpName: idp.name }}
          textType={TextType.Paragraph}
          value={messages.unlinkConfirmationWarningForNativeSsoSellerMessage}
        />
      </div>
    );
  };

  return (
    <div key={id} className={style.row}>
      <div className={style['icon-name']}>
        <div className={style.icon}>
          <SsoProviderImage logoAltText={name} logoUrl={logoUrl} />
          <Text textType={TextType.Label} value={name} />
        </div>
        {email && (
          <div className={style.email}>
            <Text formattedMessageValues={{ email }} textType={TextType.Paragraph} value={messages.connectedTo} />
          </div>
        )}
      </div>
      <Button className={style.button} onClick={onClick} theme={THEME_CONSTANTS.tertiary}>
        <div className={style['button-text']}>
          <FormattedMessage {...actionText} />
          <div className={style['external-link-icon']}>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </div>
        </div>
      </Button>
      {isSsoDisconnectConfirmOpen && (
        <ConfirmDialogueModal
          body={<ConfirmModalBody />}
          buttonMessage={<FormattedMessage {...messages.disconnect} />}
          buttonTheme='primary'
          header={<FormattedMessage {...messages.unlinkConfirmationTitleMessage} values={{ idpName: idp.name }} />}
          onClose={closeSsoDisconnectConfirm}
          onConfirm={onDisconnectConfirmClick}
        />
      )}
      {isDoubleSsoDisconnectConfirmOpen && (
        <ConfirmDialogueModal
          body={<DoubleConfirmModalBody />}
          buttonMessage={<FormattedMessage {...messages.disconnect} />}
          buttonTheme='danger'
          header={<FormattedMessage {...messages.unlinkDoubleConfirmationTitleMessage} />}
          onClose={closeDoubleSsoDisconnectConfirm}
          onConfirm={onDoubleDisconnectConfirmClick}
        />
      )}
    </div>
  );
};

SsoAccountManagementRow.propTypes = {
  idp: PropTypes.object.isRequired,
  identities: PropTypes.array.isRequired,
  isNativeSsoSeller: PropTypes.bool.isRequired,
  onUnlinkClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
};
